import { isRecurring } from 'products-ui-components/utils/recurrence';
import getLineItemProperties from 'products-ui-components/utils/properties/getLineItemProperties';
import { calculateReportPeriodRatio } from 'products-ui-components/utils/calculations/recurringPricing';
import { calculateDiscountedLineItemPrice } from 'products-ui-components/utils/calculations/pricing';
import { roundDecimals } from 'products-ui-components/utils/calculations/rounding';
import { ANNUALLY } from 'customer-data-objects/product/BillingFrequency';
import { getLineItems } from '../subject';

// *****************************
// * HubSpot Customer Margin $$ *
// *****************************

/**
 * Returns the cost to the HubSpot customer for a single line item over the
 * specified time period
 *
 * @param {LineItemRecord} lineItem - The lineItem for which to calculate cost
 * @param {string} billingFrequency - String representation of the period of time per
 *                             which to calculate total cost.
 * @return {number} The total cost for the given line item during the specified
 * time period.
 */
const calculateLineItemCost = (lineItem, billingFrequency) => {
  const {
    quantity,
    unitCost
  } = getLineItemProperties(lineItem);
  const cost = unitCost || 0;
  if (!isRecurring(lineItem)) {
    return quantity * cost;
  }
  const costPerItemBillingPeriod = cost * quantity;
  const ratio = calculateReportPeriodRatio(lineItem, billingFrequency);
  const costPerReportBillingPeriod = costPerItemBillingPeriod * ratio;
  return costPerReportBillingPeriod;
};

/**
 * Base product cost margin calculation for other utilities
 *
 * @param {number} quantity - The number of units.
 * @param {number} unitCost - The cost per unit to seller.
 * @param {number} price - The total price of all units to buyer.
 * @param {number} decimalDigits - The number of decimal digits to round to
 *
 * @return {number} The margin of the given line item.
 */
export const calculateMargin = ({
  price = 0,
  unitCost = 0,
  quantity = 1,
  decimalDigits = 2,
  maxFractionDigits = 0
}) => {
  const digitsToRound = Math.max(decimalDigits, maxFractionDigits);
  const totalCost = roundDecimals(quantity * unitCost, digitsToRound);
  return roundDecimals(price - totalCost, digitsToRound);
};

/**
 * Returns the line item's margin
 *
 * @param {number} lineItem - The line item to calculate margin
 * @param {number} billingFrequency - String representation of the period of time per
 *                             which to calculate total margin.
 *
 * @return {number} The line item's margin.
 */
export const calculateLineItemMargin = (lineItem, billingFrequency) => {
  const lineItemPrice = calculateDiscountedLineItemPrice(lineItem, billingFrequency);
  const lineItemCost = calculateLineItemCost(lineItem, billingFrequency);
  const lineItemMargin = lineItemPrice - lineItemCost;
  return lineItemMargin;
};

/**
 * Returns the total margin of all line items, both single and recurring billed,
 * per the specified time period.
 * Total margin is calculated by subtracting the cost (to the HubSpot customer)
 * of each line item from its discounted price.
 *
 * For recurring billed line items, the price and cost are calculated per the
 * specified time period: base cost is multiplied by the number of times the price
 * rate fits into the specified billing period.
 *
 * @param {List} lineItems - The lineItems for which to calculate total margin.
 * @param {string} billingFrequency - String representation of the period of time per
 *                             which to calculate total margin.
 * @return {number} The total margin of all line items.
 */
export const calculateTotalMargin = (lineItems, billingFrequency = ANNUALLY) => {
  return getLineItems(lineItems).reduce((subtotal, lineItem) => {
    return calculateLineItemMargin(lineItem, billingFrequency) + subtotal;
  }, 0);
};